import type { IntlShape, MessageDescriptor } from '@atlassian/jira-intl';
import type { AvatarDetails } from '../../../model/types.tsx';
import { messages } from './messages.tsx';

type MessageFormatter = (intl: IntlShape) => string;

/**
 * This isn't the greatest of maps. It doesn't guarantee to actually describe all the avatars.
 * It's just the best effort to describe some of the default system avatars.
 * Trouble is that our backend doesn't return any names or alts or anything; all we have are filenames which mostly don't make much sense.
 *
 * See https://hello.atlassian.net/wiki/spaces/~hbegg/pages/1898366234/Updating+Jira+s+project+icons+to+make+them+more+accessible
 *
 * NOTE: this is just a temporary solution, this ticket: https://donut-world-dogfood.atlassian.net/browse/MCRTB-1522 will move this mapping to the back end instead
 *
 * @returns MessageDescriptor if there's an appropriate one; null if the filename is not recognized. If null then use some generic fallback.
 */
const filenameToMessageDescriptor = (filename: string): MessageDescriptor | null => {
	switch (filename) {
		case 'project_avatar_alien.svg': {
			return messages.projectAvatarAlien;
		}
		case 'project_avatar_parrot.svg': {
			return messages.projectAvatarParrot;
		}
		case 'project_avatar_cloud.svg': {
			return messages.projectAvatarCloud;
		}
		case 'project_avatar_disc.svg': {
			return messages.projectAvatarDisc;
		}
		case 'project_avatar_code.svg': {
			return messages.projectAvatarCode;
		}
		case 'project_avatar_coffee.svg': {
			return messages.projectAvatarCoffee;
		}
		case 'project_avatar_design.svg': {
			return messages.projectAvatarDesign;
		}
		case 'project_avatar_drill.svg': {
			return messages.projectAvatarDrill;
		}
		case 'project_avatar_food.svg': {
			return messages.projectAvatarFood;
		}
		case 'project_avatar_koala.svg': {
			return messages.projectAvatarKoala;
		}
		case 'project_avatar_mobile.svg': {
			return messages.projectAvatarMobile;
		}
		case 'project_avatar_money.svg': {
			return messages.projectAvatarMoney;
		}
		case 'project_avatar_website.svg': {
			return messages.projectAvatarWebsite;
		}
		case 'project_avatar_nature.svg': {
			return messages.projectAvatarNature;
		}
		case 'project_avatar_notes.svg': {
			return messages.projectAvatarNotes;
		}
		case 'project_avatar_plane.svg': {
			return messages.projectAvatarPlane;
		}
		case 'project_avatar_power.svg': {
			return messages.projectAvatarPower;
		}
		case 'project_avatar_flag.svg': {
			return messages.projectAvatarFlag;
		}
		case 'project_avatar_refresh.svg': {
			return messages.projectAvatarRefresh;
		}
		case 'project_avatar_rocket.svg': {
			return messages.projectAvatarRocket;
		}
		case 'project_avatar_science.svg': {
			return messages.projectAvatarScience;
		}
		case 'project_avatar_settings.svg': {
			return messages.projectAvatarSettings;
		}
		case 'project_avatar_spanner.svg': {
			return messages.projectAvatarSpanner;
		}
		case 'project_avatar_storm.svg': {
			return messages.projectAvatarStorm;
		}
		case 'project_avatar_support.svg': {
			return messages.projectAvatarSupport;
		}
		case 'project_avatar_yeti.svg': {
			return messages.projectAvatarYeti;
		}
		default: {
			return null;
		}
	}
};

export const getAccessibleAvatarName = (avatar: AvatarDetails): MessageFormatter => {
	if (avatar?.fileName) {
		const messageDescriptor = filenameToMessageDescriptor(avatar.fileName);
		if (messageDescriptor) {
			return (intl) => intl.formatMessage(messageDescriptor);
		}
	}

	// Custom avatars have no alt because customers do not provide any when uploading images
	return () => '';
};
